import { useTranslation } from 'react-i18next';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@insolech/ui';
import {
  EVA_DENSITY_GUIDE_COPY,
  EVA_DENSITY_GUIDE_IMAGES,
  EVA_DENSITY_GUIDE_IMAGE_HEIGHT,
  EVA_DENSITY_GUIDE_IMAGE_WIDTH,
} from './evaDensityGuide';

/**
 * EvaDensityGuideModal: the EVA reference page, opened from the "Want more
 * detail on the EVA density types?" link on the EVA panel.
 *
 * Reference material only, no form state: it reads the same in the wizard,
 * in the edit dialog (a Radix dialog nested inside another one) and in the
 * read-only viewer. The page links to its own file in a new tab, so on a
 * phone a tap opens the full-size image, pinch-zoomable, instead of a
 * 2480x3508 page squeezed into a bottom sheet. ModalContent is the only
 * scroller (max-h + overflow-y-auto); nothing here adds a second one.
 *
 * The footer Close button is there for the phone: the shared ModalFooter is
 * max-sm:sticky at the bottom of the scroller, so it stays reachable under
 * a tall page, while the card's X in the top corner scrolls away.
 */
export function EvaDensityGuideModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation(['orders', 'common']);
  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="xl">
        <ModalHeader>
          <ModalTitle>
            {t('eva_density_modal_title', {
              defaultValue: EVA_DENSITY_GUIDE_COPY.eva_density_modal_title,
            })}
          </ModalTitle>
          <ModalDescription>
            {t('eva_density_modal_description', {
              defaultValue: EVA_DENSITY_GUIDE_COPY.eva_density_modal_description,
            })}
          </ModalDescription>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <p className="text-xs text-text-muted">
            {t('eva_density_modal_hint', {
              defaultValue: EVA_DENSITY_GUIDE_COPY.eva_density_modal_hint,
            })}
          </p>
          {/* Radix unmounts the content while closed; the guard states the
              intent so a future forceMount (say, for an exit animation) does
              not start fetching the page on every EVA line render. */}
          {open
            ? EVA_DENSITY_GUIDE_IMAGES.map((img) => (
                <a
                  key={img.src}
                  href={img.src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <img
                    src={img.src}
                    alt={t(img.altKey, { defaultValue: img.altDefault })}
                    width={EVA_DENSITY_GUIDE_IMAGE_WIDTH}
                    height={EVA_DENSITY_GUIDE_IMAGE_HEIGHT}
                    loading="lazy"
                    decoding="async"
                    data-testid="eva-density-guide-image"
                    className="w-full h-auto rounded-md border border-border-subtle"
                  />
                </a>
              ))
            : null}
        </ModalBody>
        <ModalFooter>
          <Button
            variant="outline"
            size="md"
            onClick={() => onOpenChange(false)}
            data-testid="eva-density-guide-close"
          >
            {t('common:action_close', { defaultValue: 'Close' })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
